"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "appliance-energy-calculator",
  "refrigerator-generator-size-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Compressor Amp Calculator",
  category: "Installation & Setup" as const,
  mediumDescription:
    "Convert refrigerator wattage to running and starting amps, then size the dedicated circuit. Uses Ohm's law (Amps = Watts ÷ Volts), compressor surge multipliers, and NEC 210.19 continuous-load sizing (125%).",
  faqs: [
    { question: "How many amps does a typical refrigerator pull?", answer: "Modern household refrigerators run between 1.5 and 6 amps at 120V (roughly 180–725 watts). Starting surge can briefly hit 3–5x that for single-speed compressors. Inverter compressors stay closer to 1.5–2x running amps." },
    { question: "Why does NEC require a 125% safety margin?", answer: "NEC 210.19(A) requires branch circuits to be sized at 125% of the continuous load to prevent breaker nuisance-tripping and conductor overheating. A 16-amp continuous draw needs at minimum a 20-amp circuit." },
    { question: "Do refrigerators need a dedicated circuit?", answer: "NEC 210.52(B)(1) Exception 2 allows a fridge to share a small-appliance branch circuit, but a dedicated 15A or 20A circuit is strongly recommended to avoid trips when other appliances start. Many local codes now require it." },
    { question: "What's the difference between standard and inverter compressors?", answer: "Standard reciprocating compressors cycle on/off and pull a 3–5x surge at startup. Inverter compressors ramp up smoothly with a 1.5–2x surge, run quieter, and use less energy by varying speed instead of cycling." },
    { question: "Can my fridge use 240V?", answer: "Most US household fridges are designed for 120V. Some commercial and built-in models accept 240V, which halves the amperage for the same wattage and reduces voltage drop on long runs." },
    { question: "What wire gauge do I need?", answer: "NEC 310.16: 14 AWG copper for 15A circuits, 12 AWG for 20A, 10 AWG for 30A, 8 AWG for 40A. Always derate for ambient temperature and conductor count per NEC tables." },
  ],
  relatedTools: ["appliance-energy-calculator", "refrigerator-generator-size-calculator", "energy-cost-calculator"],
}

export function CompressorAmpDrawCalculatorPageClient() {
  const [watts, setWatts] = useState("150")
  const [voltage, setVoltage] = useState("120")
  const [compressorType, setCompressorType] = useState("standard")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Floors are physical: no cooling-appliance nameplate reads under 10 W, and no
    // mains supply sits below 90 V (100 V Japan / 120 V US / 230 V EU, all ±10%).
    const w = readQuantity(watts, { min: 10, max: 5000 })
    const v = readQuantity(voltage, { min: 90, max: 250 })
    if (w === null || v === null) return null

    const runningAmps = w / v

    let startingMultiplier = 4
    if (compressorType === "inverter") startingMultiplier = 1.75
    else if (compressorType === "variable-speed") startingMultiplier = 2

    const startingAmps = runningAmps * startingMultiplier
    const startingWatts = startingAmps * v

    const requiredCircuitAmps = runningAmps * 1.25

    let circuitSize = ""
    let breaker = ""
    let wireGauge = ""

    if (requiredCircuitAmps <= 15) {
      circuitSize = "15-amp circuit"
      breaker = "15A breaker"
      wireGauge = "14 AWG"
    } else if (requiredCircuitAmps <= 20) {
      circuitSize = "20-amp circuit"
      breaker = "20A breaker"
      wireGauge = "12 AWG"
    } else if (requiredCircuitAmps <= 30) {
      circuitSize = "30-amp circuit"
      breaker = "30A breaker"
      wireGauge = "10 AWG"
    } else {
      circuitSize = "40+ amp circuit"
      breaker = "40A+ breaker"
      wireGauge = "8 AWG or larger"
    }

    return {
      runningAmps,
      startingAmps,
      runningWatts: w,
      startingWatts: Math.round(startingWatts),
      requiredCircuitAmps,
      circuitSize,
      breaker,
      wireGauge,
    }
  }, [watts, voltage, compressorType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What circuit does your"
      heroTitleItalic="fridge"
      heroTitlePost="actually need?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "Ohm's law + NEC 210.19"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "W ÷ V", label: "Running amps", note: "Ohm's law" },
            { value: "3–5×", label: "Surge", note: "Standard compressor" },
            { value: "1.75×", label: "Surge", note: "Inverter" },
            { value: "125%", label: "Circuit margin", note: "NEC 210.19" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-compressor-amp-calculator")}
      cta={{
        title: "Upgrading the kitchen? Shop side-by-side fridges.",
        body: "Browse side-by-side refrigerators at Fridge.com — most ship with low-amperage inverter compressors that work on standard 15A circuits.",
        buttonLabel: "Browse side-by-side fridges",
        buttonHref: "/side-by-side-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Three inputs.
                <br />
                Real circuit spec.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Wattage from the nameplate, voltage from the outlet, compressor type from the spec sheet.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Wattage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={10}
                      max={5000}
                      step={1}
                      value={watts}
                      onChange={(e) => setWatts(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">W</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Find on the inside-fridge nameplate or spec sheet.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Voltage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={90}
                      max={250}
                      step={1}
                      value={voltage}
                      onChange={(e) => setVoltage(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">V</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Compressor type
                  </label>
                  <select
                    value={compressorType}
                    onChange={(e) => setCompressorType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="standard">Standard reciprocating (4× surge)</option>
                    <option value="inverter">Inverter (1.75× surge)</option>
                    <option value="variable-speed">Variable speed (2× surge)</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Running amperage
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.runningAmps.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">A</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `${result.circuitSize} · ${result.breaker} · ${result.wireGauge}` : "Enter wattage and voltage."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Starting surge</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.startingAmps.toFixed(1) ?? "—"} A</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Required circuit</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.requiredCircuitAmps.toFixed(1) ?? "—"} A</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Spec the right fridge"
          title="Side-by-side fridges to compare"
          description="An inverter compressor draws fewer running amps, with a softer startup surge, than a single-speed one."
          collectionType="side-by-side"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">Ohm&apos;s</em> law. NEC sizing.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Running amps = watts ÷ volts. Surge multipliers come from compressor manufacturer ratings; circuit sizing follows NEC 210.19(A) and conductor ampacity from NEC 310.16.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Running amps (Ohm's law)", d: "Amps = watts ÷ voltage. A 150W fridge on 120V draws 1.25 amps continuous." },
                { n: "02", t: "Starting surge", d: "Standard compressor: 4× running amps. Inverter: 1.75×. Variable speed: 2×. Brief but high — must not nuisance-trip the breaker." },
                { n: "03", t: "Circuit per NEC 210.19", d: "Required circuit = running amps × 125% safety margin. Round up to the next standard breaker size: 15, 20, 30, or 40A." },
                { n: "04", t: "Wire gauge per NEC 310.16", d: "14 AWG for 15A, 12 AWG for 20A, 10 AWG for 30A, 8 AWG for 40A. Derate for ambient temp and conductor count." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges"
          description="Compact-footprint configurations that work great on shared 15A small-appliance branch circuits."
          collectionType="bottom-freezer"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
